import React, { useEffect, useState } from 'react';
import withRouter from '../../components/Common/withRouter';

import Spinners from '../../components/Common/Spinner';
import { Card, CardBody, Col, Container, Row, Badge } from 'reactstrap';

import { getUserPlan } from '../../axios/subscription';
import { getAdmins } from '../../axios/admin';
import VerticalLayout from '../../components/VerticalLayout';
import { useAuthStore } from '../../zustand-store/auth.store';

const AdminLocationBillingDetail = () => {
  document.title = 'Billing Details | Seamless Slot';

  const locationId = useAuthStore((state) => state.locationId);

  const [isLoading, setIsLoading] = useState(true);
  const [billingData, setBillingData] = useState(null);

  useEffect(() => {
    localStorage.setItem('path', '/adminlocationbillingdetails');
    setBillingData(null);
    fetchAdmins();
  }, [locationId]);

  const fetchAdmins = async () => {
    try {
      setIsLoading(true);

      const res = await getAdmins(locationId);
      const admins = res?.data?.data || [];

      const businessAdmin = admins.find((admin) => admin.role === 'Business Admin');

      if (businessAdmin) {
        fetchBillingData(businessAdmin.id);
      }
    } catch (error) {
      console.error('Failed to fetch admins:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBillingData = async (adminId) => {
    try {
      const res = await getUserPlan(adminId);

      // API format: { code, message, data }
      if (res?.data) {
        setBillingData(res.data);
      }
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
    }
  };

  const formatDate = (date) =>
    new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });

  return (
    <VerticalLayout>
      <div className="page-content">
        <Container fluid>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0">Billing Details</h4>
          </div>

          {isLoading ? (
            <Spinners setLoading={setIsLoading} timerStop />
          ) : (
            <Row>
              <Col lg="12">
                <Card>
                  <CardBody>
                    {billingData ? (
                      <Row>
                        {/* PLAN INFO */}
                        <Col md="6">
                          <div className="mb-3">
                            <h6 className="text-muted mb-1">Current Plan</h6>
                            <h5>{billingData.currentPlan}</h5>
                          </div>

                          <div className="mb-3">
                            <h6 className="text-muted mb-1">Billing Interval</h6>
                            <Badge color="light" className="text-dark">
                              {billingData.interval}
                            </Badge>
                          </div>
                        </Col>

                        {/* SEATS & TOTAL */}
                        <Col md="6">
                          <div className="mb-3">
                            <h6 className="text-muted mb-1">Total Seats</h6>
                            <h5>{billingData.totalSeats}</h5>
                          </div>

                          <div className="mb-3">
                            <h6 className="text-muted mb-1">Total Amount</h6>
                            <h5>
                              {billingData.subscriptionPayment?.[0]?.currency || 'USD'}{' '}
                              {billingData.totalAmount}
                            </h5>
                          </div>
                        </Col>

                        {/* PAYMENT HISTORY */}
                        <Col md="12">
                          <hr />
                          <h6 className="text-muted mb-3">Payment History</h6>

                          {billingData.subscriptionPayment?.length > 0 ? (
                            billingData.subscriptionPayment.map((payment) => (
                              <Card key={payment.id} className="mb-3 border">
                                <CardBody>
                                  <Row>
                                    <Col md="6" className="mb-2">
                                      <small className="text-muted">Payment ID</small>
                                      <div className="fw-semibold">{payment.id}</div>
                                    </Col>

                                    <Col md="6" className="mb-2">
                                      <small className="text-muted">Status</small>
                                      <div>
                                        <Badge color="success">Paid</Badge>
                                      </div>
                                    </Col>

                                    <Col md="6" className="mb-2">
                                      <small className="text-muted">Amount</small>
                                      <div className="fw-semibold">
                                        {payment.currency} {payment.amount}
                                      </div>
                                    </Col>

                                    <Col md="6" className="mb-2">
                                      <small className="text-muted">Payment Date</small>
                                      <div className="fw-semibold">{formatDate(payment.date)}</div>
                                    </Col>

                                    <Col md="12">
                                      <small className="text-muted">Square Payment ID</small>
                                      <div
                                        className="fw-semibold text-break"
                                        title={payment.squarePaymentId}
                                      >
                                        {payment.squarePaymentId}
                                      </div>
                                    </Col>
                                  </Row>
                                </CardBody>
                              </Card>
                            ))
                          ) : (
                            <p className="text-muted mb-0">No payments recorded</p>
                          )}
                        </Col>
                      </Row>
                    ) : (
                      <p className="text-muted">No billing data available</p>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </div>
    </VerticalLayout>
  );
};

export default withRouter(AdminLocationBillingDetail);
